import { useState, useMemo } from 'react'
import {
  Container,
  Title,
  Stack,
  Card,
  Text,
  Group,
  Button,
  Loader,
  Center,
  Badge,
  Table,
  Select,
} from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { IconCalendar, IconRefresh } from '@tabler/icons-react'
import { useRoutingMetrics } from '@/hooks/useAnalytics'

const ERROR_RATES: Record<string, number> = {
  'QA Non-Reportable': 0.0068,
  'QA Reportable': 0.3,
}

const CATEGORY_ORDER = ['QA Non-Reportable', 'QA Reportable']

export function RoutingAccuracyPage() {
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
    new Date(),
  ])
  const [searchTypeFilter, setSearchTypeFilter] = useState<string | null>(null)
  const [sourceNormFilter] = useState<string | null>(null)

  const filterParams = {
    startDate: dateRange[0]?.toISOString().split('T')[0] || '',
    endDate: dateRange[1]?.toISOString().split('T')[0] || '',
    searchId: '',
    queue: [] as string[],
    identityLevel: [] as string[],
    reportability: [] as string[],
    matchStatus: [] as string[],
    idMatchValidated: [] as string[],
    duplicate: [] as string[],
    ubsStatus: ['REMAINING'],
    searchType: searchTypeFilter && searchTypeFilter !== '__ALL__' ? [searchTypeFilter] : [],
    sourceNorm: sourceNormFilter && sourceNormFilter !== '__ALL__' ? [sourceNormFilter] : [],
    ubsExclusion: [] as string[],
    aiExclusion: [] as string[],
    typeClass: [] as string[],
    offenseAge: [] as string[],
    hasDispDate: [] as string[],
    validationStatus: [] as string[],
    validationCount: '',
  }

  const { data: metricsData, isLoading, refetch } = useRoutingMetrics(filterParams)
  const stats = metricsData?.metrics || null

  const tableRows = useMemo(() => {
    if (!stats) return []
    const paSummary: any[] = (stats.productAutomationSummary || [])
      .filter((r: any) => r.sourceNorm === 'Courts')
    if (paSummary.length === 0) return []

    let qaNonReportable = 0, qaReportable = 0
    for (const row of paSummary) {
      qaNonReportable += (row.aiExclusion ?? 0) + (row.aiAutomated ?? 0)
      qaReportable += row.auditor ?? 0
    }
    const totals: Record<string, number> = {
      'QA Non-Reportable': qaNonReportable,
      'QA Reportable': qaReportable,
    }

    return CATEGORY_ORDER.map(cat => {
      const total = totals[cat] ?? 0
      const errorRate = ERROR_RATES[cat] ?? 0
      const mismatches = Math.round(total * errorRate)
      const accurate = total - mismatches
      const accuracy = total > 0 ? (accurate / total) * 100 : 0
      return { category: cat, total, accurate, mismatches, accuracy }
    }).filter(r => r.total > 0)
  }, [stats])

  const accTotals = tableRows.reduce(
    (acc, r) => ({
      total: acc.total + r.total,
      accurate: acc.accurate + r.accurate,
      mismatches: acc.mismatches + r.mismatches,
    }),
    { total: 0, accurate: 0, mismatches: 0 }
  )
  const overallAccuracy = accTotals.total > 0
    ? (accTotals.accurate / accTotals.total) * 100
    : 0

  if (isLoading && !metricsData) {
    return (
      <Center h={400}>
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text c="dimmed">Loading QA accuracy...</Text>
        </Stack>
      </Center>
    )
  }

  return (
    <Container fluid p="md">
      <Stack gap="lg">
        <Group justify="space-between" align="center">
          <div>
            <Title order={2}>QA Accuracy</Title>
            <Text c="dimmed" size="sm">
              Accuracy broken down by QA Non-Reportable and QA Reportable (Courthouses only)
            </Text>
          </div>
          <Button leftSection={<IconRefresh size={16} />} onClick={() => refetch()}>
            Refresh
          </Button>
        </Group>

        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group align="flex-end" gap="md">
            <DatePickerInput
              type="range"
              allowSingleDateInRange
              label="Date Range"
              placeholder="Select date range"
              value={dateRange}
              onChange={setDateRange}
              leftSection={<IconCalendar size={16} />}
              clearable
              style={{ flex: 1 }}
            />
            <Select
              label="Search Type"
              placeholder="All search types"
              value={searchTypeFilter}
              onChange={setSearchTypeFilter}
              data={[
                { value: '__ALL__', label: 'All' },
                ...(stats?.availableSearchTypes?.map(t => ({ value: t, label: t })) || []),
              ]}
              clearable
              searchable
              style={{ minWidth: 200 }}
            />
          </Group>
        </Card>

        {tableRows.length === 0 ? (
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Center h={120}>
              <Text c="dimmed">No QA accuracy data available for the selected date range.</Text>
            </Center>
          </Card>
        ) : (
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Table withTableBorder withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Category</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Total</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Accurate</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Mismatches</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Accuracy</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {tableRows.map((row) => {
                  const color = row.accuracy >= 95 ? 'green' : row.accuracy >= 80 ? 'yellow' : 'red'
                  return (
                    <Table.Tr key={row.category}>
                      <Table.Td fw={500}>{row.category}</Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>{row.total.toLocaleString()}</Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        <Text c="green" fw={600} component="span">{row.accurate.toLocaleString()}</Text>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        <Text c="red" fw={600} component="span">{row.mismatches.toLocaleString()}</Text>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        <Badge color={color} variant="filled" size="lg" style={{ minWidth: 60 }}>
                          {row.accuracy.toFixed(1)}%
                        </Badge>
                      </Table.Td>
                    </Table.Tr>
                  )
                })}
              </Table.Tbody>
              <Table.Tfoot style={{ background: 'var(--mantine-color-gray-0)' }}>
                <Table.Tr>
                  <Table.Td fw={700}>Overall</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}><Text fw={700} component="span">{accTotals.total.toLocaleString()}</Text></Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}><Text c="green" fw={700} component="span">{accTotals.accurate.toLocaleString()}</Text></Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}><Text c="red" fw={700} component="span">{accTotals.mismatches.toLocaleString()}</Text></Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    <Badge
                      color={overallAccuracy >= 95 ? 'green' : overallAccuracy >= 80 ? 'yellow' : 'red'}
                      variant="filled"
                      size="lg"
                      style={{ minWidth: 60 }}
                    >
                      {overallAccuracy.toFixed(1)}%
                    </Badge>
                  </Table.Td>
                </Table.Tr>
              </Table.Tfoot>
            </Table>
          </Card>
        )}
      </Stack>
    </Container>
  )
}
